/**
 * AuditTrail — the shared, admin-only per-object change-history surface.
 *
 * Usage in any list/editor screen:
 *   const audit = useAuditTrails();
 *   // in a row's ⋯ menu items:  ...(audit.auditItem("event", ev.id, ev.title) ? [audit.auditItem(...)] : [])
 *   // once in the screen body:  {audit.drawer}
 *
 * `auditItem` returns null for users without the capability, so the entry only
 * appears where it's allowed. #803 (2026-07-13, product decision — flagged to
 * the operator in the changelog): the per-object trail is capability-gated on
 * `audit.read_object`, granted by default to Event Manager (the event-admin
 * role) as well as System Administrator. The workspace-wide Audit LOG screen
 * (settings.audit, `audit.read`) stays System-Administrator-only.
 * The drawer opens from the right, lists entries newest-first,
 * and each row expands to field-level before → after diffs. Export CSV / PDF.
 */
import { useState, type CSSProperties } from "react";
import { Avatar, Btn, Icon, IconBtn, T, alpha } from "@v0y4ge/jrni-ui";
import { can, isAdmin } from "./access";
import {
  useObjectAudit, AUDIT_TYPE_LABEL, exportAuditCsv, exportAuditPdf,
  humanizeField, formatValue, formatWhen, sourceLabel,
  type AuditObjectType, type AuditEntry,
} from "../lib/audit";
import { useT } from "../lib/i18n";

const ACTION_TONE: Record<string, string> = {
  created: T.green1, published: T.green1, restored: T.green1,
  updated: T.p1, duplicated: T.p1,
  unpublished: T.orange1, cancelled: T.orange1, archived: T.orange1,
  deleted: T.red1,
};

function ActionPill({ action }: { action: string }) {
  const color = ACTION_TONE[action] || T.n2;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, textTransform: "capitalize", color, background: color + "1f", whiteSpace: "nowrap" }}>{action}</span>
  );
}

function EntryCard({ e }: { e: AuditEntry }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const hasChanges = e.changes.length > 0;
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, overflow: "hidden" }}>
      {/* ui-raw: version disclosure row — a full-width, TWO-LINE row (version chip, action pill, actor avatar). Button is a single-line control on a fixed height scale; Disclosure is the real candidate. */}
      <button type="button" onClick={() => hasChanges && setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "11px 12px", border: "none", background: "transparent", cursor: hasChanges ? "pointer" : "default", fontFamily: T.font }}>
        <span style={{ width: 34, height: 24, borderRadius: 6, background: T.n6, color: T.n2, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: T.fontMono }}>{t("shell.auditTrail.v", "v")}{" "}{e.version}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ActionPill action={e.action} />
            {hasChanges ? <span style={{ fontSize: 12, color: T.soft }}>{t("shell.auditTrail.fieldCount.icu", { count: e.changes.length })}</span> : null}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5, fontSize: 12, color: T.soft, minWidth: 0 }}>
            <Avatar size={18} initials={e.actor.init} color={e.actor.color} />
            <span style={{ color: T.n2, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.actor.name}</span>
            {e.actor.onBehalfOf ? (
              <span title={`Acting as ${e.actor.onBehalfOf.name}${e.actor.onBehalfOf.email ? ` (${e.actor.onBehalfOf.email})` : ""}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, fontSize: 11, fontWeight: 700, color: T.orange1, background: alpha(T.orange1, 10), border: `1px solid ${alpha(T.orange1, 28)}`, borderRadius: T.radius.pill, padding: "1px 8px", whiteSpace: "nowrap" }}>
                acting as {e.actor.onBehalfOf.name}
              </span>
            ) : null}
            <span>·</span><span style={{ whiteSpace: "nowrap" }}>{formatWhen(e.at)}</span>
            <span>·</span><span>{sourceLabel(e.source)}</span>
          </span>
        </span>
        {hasChanges ? <Icon.chev dir={open ? "up" : "down"} size={14} /> : null}
      </button>
      {open && hasChanges ? (
        <div style={{ borderTop: `1px solid ${T.border}`, background: T.canvas, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
          {e.changes.map((c, i) => (
            <div key={i} style={{ fontSize: 12.5, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontWeight: 700, color: T.n1 }}>{humanizeField(c.field)}</span>
              <span style={{ color: T.soft, textDecoration: "line-through" }}>{formatValue(c.from)}</span>
              <Icon.arrow dir="right" size={11} />
              <span style={{ color: T.n1, fontWeight: 500 }}>{formatValue(c.to)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AuditTrailDrawer({ target, onClose }: { target: { type: AuditObjectType; id: string; name: string }; onClose: () => void }) {
  const t = useT();
  const entries = useObjectAudit(target.type, target.id);
  const panel: CSSProperties = { position: "fixed", top: 0, right: 0, bottom: 0, width: 460, maxWidth: "100vw", background: T.white, boxShadow: T.shadowLg, zIndex: 260, display: "flex", flexDirection: "column", fontFamily: T.font };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 255, background: "rgba(20,16,46,0.4)" }}>
      <div onClick={(ev) => ev.stopPropagation()} style={panel}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.soft }}>{AUDIT_TYPE_LABEL[target.type]} {t("shell.auditTrail.auditTrail", "· Audit trail")}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{target.name || "Untitled"}</div>
          </div>
          <IconBtn size={32} onClick={onClose} label={t("common.actions.close", "Close")}><Icon.x size={14} /></IconBtn>
        </div>
        <div style={{ padding: "10px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 12.5, color: T.soft }}>{entries.length} {entries.length === 1 ? t('shell.auditTrail.text') : t('shell.auditTrail.text2')}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn variant="secondary" size="sm" icon={<Icon.csv size={13} />} disabled={!entries.length} onClick={() => exportAuditCsv(target.name || "object", entries)}>{t("shell.auditTrail.csv", "CSV")}</Btn>
            <Btn variant="secondary" size="sm" icon={<Icon.download size={13} />} disabled={!entries.length} onClick={() => exportAuditPdf(`${AUDIT_TYPE_LABEL[target.type]} · ${target.name || "Untitled"}`, entries)}>{t("shell.auditTrail.pdf", "PDF")}</Btn>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 8, background: T.canvas }}>
          {entries.length === 0 ? (
            <div style={{ padding: "48px 20px", textAlign: "center", color: T.soft, fontSize: 13 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: T.n6, color: T.n3, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}><Icon.list size={20} /></div>
              <div style={{ fontWeight: 600, color: T.n2 }}>{t("shell.auditTrail.noChangesRecordedYet", "No changes recorded yet")}</div>
              <div style={{ marginTop: 4, lineHeight: 1.5 }}>{t("shell.auditTrail.everyEditToThis", "Every edit to this")}{" "}{AUDIT_TYPE_LABEL[target.type].toLowerCase()} {t("shell.auditTrail.willBeLoggedHereWhoWhenAndWhatChanged", "will be logged here — who, when, and what changed.")}</div>
            </div>
          ) : entries.map((e) => <EntryCard key={e.id} e={e} />)}
        </div>
      </div>
    </div>
  );
}

export interface AuditMenuItem { icon: React.ReactNode; label: string; onClick: () => void }

/** One hook per screen: gates on `audit.read_object` (Event Manager + System
 * Administrator by default — #803), provides the ⋯ menu item + the drawer. */
export function useAuditTrails() {
  const t = useT();
  const canAudit = isAdmin() || can("audit.read_object");
  const [target, setTarget] = useState<{ type: AuditObjectType; id: string; name: string } | null>(null);
  const openAudit = (type: AuditObjectType, id: string, name: string) => setTarget({ type, id, name });
  const auditItem = (type: AuditObjectType, id: string, name: string): AuditMenuItem | null =>
    canAudit ? { icon: <Icon.list size={14} />, label: t("shell.auditTrail.viewAuditTrail", "View audit trail"), onClick: () => openAudit(type, id, name) } : null;
  const drawer = target ? <AuditTrailDrawer target={target} onClose={() => setTarget(null)} /> : null;
  return { canAudit, openAudit, auditItem, drawer };
}
